(() => {
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function reportCatalogSearchError(err) {
    try {
      document.dispatchEvent(new CustomEvent("pma:observability", {
        detail: { kind: "error", error_type: "commerce_catalog_search", message: (err && err.message) || String(err) },
        bubbles: true,
      }));
    } catch {}
  }

  function rebindCatalogCommerceUi(root) {
    const rebind = window.PMA?.commerce?.rebind;
    if (typeof rebind === "function") {
      rebind(root);
      return;
    }
    window.setTimeout(() => {
      const retry = window.PMA?.commerce?.rebind;
      if (typeof retry === "function") retry(root);
    }, 0);
  }

  // Canonical Marketplace keyword search: visible state stays on /collections/all,
  // while Shopify's native /search route remains the server-rendered result source.
  const PMA_CATALOG_PATH = "/collections/all";
  const PMA_SEARCH_PATH = "/search";
  const PMA_SEARCH_SORTS = new Set(["relevance", "price-ascending", "price-descending"]);
  let pmaCatalogSearchRequestId = 0;

  function isCanonicalCatalogPath(pathname = window.location.pathname) {
    const clean = String(pathname || "").replace(/\/+$/, "") || "/";
    return clean === PMA_CATALOG_PATH;
  }

  function isCanonicalCatalogSearchActive() {
    if (!isCanonicalCatalogPath()) return false;
    try {
      return String(new URL(window.location.href).searchParams.get("q") || "").trim() !== "";
    } catch {
      return false;
    }
  }

  function pmaCatalogParamAllowed(name) {
    return (
      name === "q" ||
      name === "sort_by" ||
      name === "page" ||
      name === "preview_theme_id" ||
      String(name || "").startsWith("filter.")
    );
  }

  function normalizeCanonicalCatalogUrl(input) {
    const source = new URL(input, window.location.origin);
    const target = new URL(PMA_CATALOG_PATH, window.location.origin);
    source.searchParams.forEach((value, name) => {
      if (pmaCatalogParamAllowed(name)) target.searchParams.append(name, value);
    });

    const query = String(target.searchParams.get("q") || "").trim();
    if (query) {
      target.searchParams.set("q", query);
      const sort = String(target.searchParams.get("sort_by") || "");
      if (sort && !PMA_SEARCH_SORTS.has(sort)) target.searchParams.delete("sort_by");
      const locked = document.body?.classList?.contains("is-member-locked");
      if (locked && /^price-(ascending|descending)$/.test(String(target.searchParams.get("sort_by") || ""))) {
        target.searchParams.set("sort_by", "relevance");
        target.searchParams.delete("page");
      }
      target.hash = "#shop-products";
    } else {
      target.searchParams.delete("q");
      if (target.searchParams.get("sort_by") === "relevance") target.searchParams.delete("sort_by");
      target.hash = source.hash || "";
    }
    return target;
  }

  function buildCatalogSearchPageUrl(canonicalInput) {
    const canonical = normalizeCanonicalCatalogUrl(canonicalInput);
    const searchUrl = new URL(PMA_SEARCH_PATH, window.location.origin);

    canonical.searchParams.forEach((value, name) => {
      if (pmaCatalogParamAllowed(name)) {
        searchUrl.searchParams.append(name, value);
      }
    });

    searchUrl.searchParams.set("type", "product");
    return searchUrl;
  }

  function canonicalCatalogUrlFromLink(anchor) {
    const raw = anchor?.getAttribute("href") || "";
    if (!raw || raw.startsWith("#")) return null;
    let source;
    try {
      source = new URL(raw, window.location.href);
    } catch {
      return null;
    }
    if (source.origin !== window.location.origin) return null;
    const sourcePath = String(source.pathname || "").replace(/\/+$/, "") || "/";
    if (sourcePath !== PMA_SEARCH_PATH && sourcePath !== PMA_CATALOG_PATH) return null;

    const target = normalizeCanonicalCatalogUrl(source);
    const current = new URL(window.location.href);
    const sourceHasFilter = Array.from(source.searchParams.keys()).some((name) => name.startsWith("filter."));
    if (
      sourcePath === PMA_CATALOG_PATH &&
      !target.searchParams.get("q") &&
      sourceHasFilter &&
      String(current.searchParams.get("q") || "").trim()
    ) {
      target.searchParams.set("q", String(current.searchParams.get("q") || "").trim());
      target.hash = "#shop-products";
      return normalizeCanonicalCatalogUrl(target);
    }
    return target;
  }

  function stampCanonicalProductReturnUrls(surface, canonicalInput) {
    const canonical = normalizeCanonicalCatalogUrl(canonicalInput);
    const returnUrl =
      canonical.pathname +
      canonical.search +
      (canonical.hash || "#shop-products");

    qsa('a[href*="/products/"]', surface).forEach((anchor) => {
      const raw = anchor.getAttribute("href") || "";
      if (!raw) return;

      let url;
      try {
        url = new URL(raw, window.location.origin);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (!/(^|\/)products\//.test(url.pathname)) return;

      url.searchParams.set("pma_return", returnUrl);
      url.searchParams.set("pma_return_context", "shop");

      anchor.setAttribute(
        "href",
        url.pathname + url.search + url.hash,
      );
    });
  }

  function extractCatalogSearchSurface(html, canonicalInput) {
    const doc = new DOMParser().parseFromString(html, "text/html");

    const template = doc.querySelector(
      "template[data-pma-catalog-search-template]",
    );

    const surface = template?.content?.querySelector(
      "[data-pma-catalog-search-surface]",
    );

    if (!template || !surface) {
      throw new Error("catalog_search_surface_missing");
    }

    const cloned = surface.cloneNode(true);
    stampCanonicalProductReturnUrls(cloned, canonicalInput);

    return cloned;
  }

  async function fetchCatalogSearchHtml(url) {
    const response = await fetch(url.pathname + url.search, {
      credentials: "same-origin",
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error(
        "catalog_search_fetch_failed_" + response.status,
      );
    }

    return response.text();
  }

  async function fetchCanonicalCatalogSearchSurface(canonicalInput) {
    /*
     * Always render the genuine Shopify search page.
     *
     * The former dynamic section-id optimization created a separate
     * second-request path after the first keyword search. That path
     * produced inconsistent facet counts and was the only path used
     * by search -> filter apply.
     *
     * Using one source for every interaction keeps search.results,
     * search.filters, sort and pagination in the same Shopify context.
     */
    const searchUrl = buildCatalogSearchPageUrl(canonicalInput);
    const html = await fetchCatalogSearchHtml(searchUrl);

    return extractCatalogSearchSurface(
      html,
      canonicalInput,
    );
  }

  function replaceCatalogFiltersFromSearchSurface(surface) {
    const filtersSection = document.querySelector(
      '[data-section-handle="pma__commerce__filters_panel"]',
    );
    if (!filtersSection) return;

    const hasFilters =
      surface.getAttribute("data-has-filters") === "true";

    /*
     * Core hoists the real filters drawer into #PmaOverlayRoot and
     * keeps event-handler references to that drawer node.
     *
     * Never replace that outer node. Doing so leaves the Filter
     * trigger wired to the old collection drawer.
     */
    const sourceSidebar = surface.querySelector(
      "[data-pma-search-bridge-sidebar] .pma-filters",
    );
    const targetSidebar = filtersSection
      .querySelector(".pma-container")
      ?.querySelector(".pma-filters");

    if (sourceSidebar && targetSidebar) {
      targetSidebar.replaceWith(sourceSidebar.cloneNode(true));
    }

    const sourceDrawer = surface.querySelector(
      "[data-pma-search-bridge-drawer] #PmaFiltersDrawer",
    );

    const targetDrawer =
      document.querySelector(
        "#PmaOverlayRoot #PmaFiltersDrawer",
      ) ||
      filtersSection.querySelector("#PmaFiltersDrawer");

    if (sourceDrawer && targetDrawer) {
      const sourceBody =
        sourceDrawer.querySelector(".pma-drawer__body");
      const targetBody =
        targetDrawer.querySelector(".pma-drawer__body");

      if (!sourceBody || !targetBody) {
        throw new Error(
          "catalog_search_drawer_body_missing",
        );
      }

      /*
       * Preserve the outer drawer, header close button, backdrop,
       * focus trap and core toggle references. Only swap the facet
       * content.
       */
      targetBody.innerHTML = sourceBody.innerHTML;

      targetDrawer.setAttribute(
        "data-pma-catalog-mode",
        "search",
      );

      const sourceLabel =
        sourceDrawer.getAttribute("aria-label");
      if (sourceLabel) {
        targetDrawer.setAttribute(
          "aria-label",
          sourceLabel,
        );
      }
    }

    /*
     * Older adapter versions could append a duplicate drawer inside
     * the filters section after Core had hoisted the real drawer.
     * Remove any such stale duplicate. Never remove the hoisted node.
     */
    qsa("#PmaFiltersDrawer", filtersSection).forEach(
      (drawer) => {
        if (drawer !== targetDrawer) drawer.remove();
      },
    );

    filtersSection.hidden = !hasFilters;
    filtersSection.setAttribute(
      "data-pma-catalog-mode",
      "search",
    );

    if (hasFilters) {
      rebindCatalogCommerceUi(filtersSection);

      if (
        targetDrawer &&
        !filtersSection.contains(targetDrawer)
      ) {
        rebindCatalogCommerceUi(targetDrawer);
      }
    }
  }

  function replaceCatalogToolbarFromSearchSurface(surface) {
    const toolbarSection = document.querySelector('[data-section-handle="pma__commerce__collection_toolbar"]');
    if (!toolbarSection) return;
    const query = surface.getAttribute("data-query") || "";
    const countLabel = surface.getAttribute("data-results-count-label") || "";
    const activeCount = Number(surface.getAttribute("data-active-filter-count") || "0") || 0;
    const hasFilters = surface.getAttribute("data-has-filters") === "true";

    qsa('[data-pma-catalog-search-entry] input[name="q"]').forEach((input) => {
      input.value = query;
    });

    const resultsCount = toolbarSection.querySelector(
      "[data-pma-results-count], .pma-collection-toolbar__count, .pma-results-count",
    );
    if (resultsCount && countLabel) resultsCount.textContent = countLabel;

    const bridgeSort = surface.querySelector("[data-pma-search-bridge-sort]");
    const collectionSort = toolbarSection.querySelector("select[data-pma-collection-sort]");
    if (bridgeSort && collectionSort) {
      collectionSort.innerHTML = bridgeSort.innerHTML;
      collectionSort.value = bridgeSort.value;
    }

    qsa("[data-pma-filter-trigger], .pma-filter-trigger", toolbarSection).forEach((trigger) => {
      trigger.hidden = !hasFilters;
      if (!hasFilters) return;
      const label = activeCount > 0 ? `Filters (${activeCount})` : "Filters";
      const textNode = trigger.querySelector("[data-pma-filter-trigger-label]");
      if (textNode) textNode.textContent = label;
      else if (!trigger.querySelector(".pma-icon")) trigger.textContent = label;
      trigger.setAttribute("aria-label", activeCount > 0 ? `${label}, active filters` : label);
    });

    let applied = toolbarSection.querySelector(".pma-collection-toolbar__applied");
    const bridgeApplied = surface.querySelector("[data-pma-search-bridge-applied]");
    const appliedHtml = bridgeApplied?.innerHTML?.trim() || "";
    if (appliedHtml) {
      if (!applied) {
        applied = document.createElement("div");
        applied.className = "pma-collection-toolbar__applied";
        const toolbar = toolbarSection.querySelector(".pma-collection-toolbar");
        if (toolbar) toolbar.insertAdjacentElement("afterend", applied);
        else toolbarSection.querySelector(".pma-container")?.appendChild(applied);
      }
      if (applied) applied.innerHTML = appliedHtml;
    } else if (applied) {
      applied.remove();
    }
    toolbarSection.setAttribute("data-pma-catalog-mode", "search");
  }

  function replaceCatalogGridFromSearchSurface(surface) {
    const gridSection = document.querySelector('[data-section-handle="pma__commerce__product_grid"]');
    const targetStack = gridSection?.querySelector("[data-pma-product-grid-stack]");
    const sourceStack = surface.querySelector("[data-pma-search-bridge-grid-stack] [data-pma-product-grid-stack]");
    if (!gridSection || !targetStack || !sourceStack) {
      throw new Error("catalog_search_grid_target_missing");
    }
    targetStack.innerHTML = sourceStack.innerHTML;
    gridSection.setAttribute("data-pma-catalog-mode", "search");
    rebindCatalogCommerceUi(gridSection);
    document.dispatchEvent(
      new CustomEvent("pma:catalog-search-rendered", {
        bubbles: true,
        detail: { query: surface.getAttribute("data-query") || "" },
      }),
    );
  }

  function applyCanonicalCatalogSearchSurface(surface) {
    replaceCatalogToolbarFromSearchSurface(surface);
    replaceCatalogFiltersFromSearchSurface(surface);
    replaceCatalogGridFromSearchSurface(surface);
  }

  async function navigateCanonicalCatalogSearch(
    input,
    { historyMode = "none", scroll = false } = {},
  ) {
    const canonical = normalizeCanonicalCatalogUrl(input);
    const query = String(canonical.searchParams.get("q") || "").trim();
    if (!query) {
      window.location.assign(canonical.pathname + canonical.search + canonical.hash);
      return false;
    }

    const requestId = ++pmaCatalogSearchRequestId;
    const gridSection = document.querySelector('[data-section-handle="pma__commerce__product_grid"]');
    gridSection?.setAttribute("aria-busy", "true");
    try {
      const surface = await fetchCanonicalCatalogSearchSurface(canonical);
      if (requestId !== pmaCatalogSearchRequestId) return false;
      applyCanonicalCatalogSearchSurface(surface);

      const visibleUrl = canonical.pathname + canonical.search + canonical.hash;
      if (historyMode === "push") window.history.pushState({ pmaCatalogSearch: true }, "", visibleUrl);
      else if (historyMode === "replace") window.history.replaceState({ pmaCatalogSearch: true }, "", visibleUrl);

      if (scroll) document.getElementById("shop-products")?.scrollIntoView({ block: "start" });
      return true;
    } catch (err) {
      if (requestId !== pmaCatalogSearchRequestId) return false;

      reportCatalogSearchError(err);

      /*
       * Never escape a JS-enhanced canonical Marketplace session to
       * /search because an internal refresh failed. The forms retain
       * their native /search action for genuine no-JS fallback.
       *
       * With JavaScript active, keep the last valid catalog surface
       * in place and expose the actual error in the console.
       */
      try {
        console.error(
          "[PMA catalog search] canonical refresh failed",
          err,
          canonical.pathname + canonical.search + canonical.hash,
        );
      } catch {}

      return false;
    } finally {
      if (requestId === pmaCatalogSearchRequestId) gridSection?.removeAttribute("aria-busy");
    }
  }

  function canonicalCatalogUrlFromFilterForm(form) {
    const current = new URL(window.location.href);
    const target = new URL(PMA_CATALOG_PATH, window.location.origin);
    const preview = current.searchParams.get("preview_theme_id");
    if (preview) target.searchParams.set("preview_theme_id", preview);
    const values = new FormData(form);
    const seenRepeated = new Set();
    for (const [name, rawValue] of values.entries()) {
      if (!pmaCatalogParamAllowed(name) || name === "page") continue;
      const value = String(rawValue || "").trim();
      if (!value) continue;
      if (name === "q" || name === "sort_by" || name === "preview_theme_id") {
        target.searchParams.set(name, value);
        continue;
      }
      if (!seenRepeated.has(name)) {
        target.searchParams.delete(name);
        seenRepeated.add(name);
      }
      target.searchParams.append(name, value);
    }
    // While search mode is active, the visible canonical URL is the
    // authority for q. A rendered filter form must never erase it.
    const currentQuery = String(
      current.searchParams.get("q") || "",
    ).trim();

    if (currentQuery) {
      target.searchParams.set("q", currentQuery);
    }

    target.searchParams.delete("page");
    target.hash = "#shop-products";
    return normalizeCanonicalCatalogUrl(target);
  }

  function bindCanonicalCatalogSearchInteractions() {
    if (document.documentElement.dataset.pmaCatalogSearchBound === "1") return;
    document.documentElement.dataset.pmaCatalogSearchBound = "1";

    document.addEventListener(
      "submit",
      (event) => {
        if (!isCanonicalCatalogSearchActive()) return;
        const form = event.target?.closest?.("form[data-pma-filters-form]");
        if (!form) return;
        const filtersOwner =
          form.closest(
            '[data-section-handle="pma__commerce__filters_panel"]',
          ) ||
          form.closest("#PmaFiltersDrawer");

        if (!filtersOwner) return;

        event.preventDefault();

        const target = canonicalCatalogUrlFromFilterForm(form);

        /*
         * Canonical search filtering is AJAX-enhanced, so unlike a
         * normal collection navigation the open PMA drawer is not
         * destroyed by a page load. Close it through Core's existing
         * overlay control before starting the refresh.
         *
         * That preserves Core's backdrop, scroll-lock, aria and focus
         * cleanup instead of duplicating overlay state here.
         */
        const openDrawer = form.closest("#PmaFiltersDrawer.is-open");
        const closeButton = openDrawer?.querySelector(
          "[data-overlay-close]",
        );

        if (closeButton) {
          closeButton.click();
        }

        navigateCanonicalCatalogSearch(target, {
          historyMode: "push",
          scroll: true,
        });
      },
      true,
    );

    document.addEventListener(
      "click",
      (event) => {
        if (!isCanonicalCatalogSearchActive()) return;
        const anchor = event.target?.closest?.("a[href]");
        if (!anchor) return;
        const owner = anchor.closest(
          '[data-section-handle="pma__commerce__collection_toolbar"], [data-section-handle="pma__commerce__filters_panel"], [data-section-handle="pma__commerce__product_grid"], #PmaFiltersDrawer',
        );
        if (!owner) return;
        const target = canonicalCatalogUrlFromLink(anchor);
        if (!target) return;
        event.preventDefault();
        if (target.searchParams.get("q")) {
          try {
            if (window.PMA?.analytics?.track) {
              if (anchor.classList.contains("pma-applied-filters__chip-remove")) {
                window.PMA.analytics.track("pma_filter_clear", { filter_action: "remove_one", search_ui: "catalog" });
              } else if (anchor.classList.contains("pma-applied-filters__clear-link") || anchor.getAttribute("data-pma-cta") === "filters_cleared") {
                window.PMA.analytics.track("pma_filter_clear", { filter_action: "clear_all", search_ui: "catalog" });
              }
            }
          } catch {}
          if (anchor.closest(".pma-pagination")) {
            try {
              if (window.PMA?.analytics?.track) {
                const pageNum = parseInt(target.searchParams.get("page") || "1", 10) || 1;
                const isPrev = anchor.classList.contains("pma-page--prev") || anchor.getAttribute("aria-label")?.toLowerCase().includes("previous");
                const isNext = anchor.classList.contains("pma-page--next") || anchor.getAttribute("aria-label")?.toLowerCase().includes("next");
                window.PMA.analytics.track("pma_pagination", {
                  pagination_mode: "numbered",
                  page_number: pageNum,
                  action: isPrev ? "prev" : isNext ? "next" : "page",
                });
              }
            } catch {}
          }
          navigateCanonicalCatalogSearch(target, { historyMode: "push", scroll: true });
        } else {
          window.location.assign(target.pathname + target.search + target.hash);
        }
      },
      true,
    );
  }

  async function initCanonicalCatalogSearch() {
    if (!isCanonicalCatalogPath()) return;
    const current = new URL(window.location.href);
    const normalized = normalizeCanonicalCatalogUrl(current);
    const currentVisible = current.pathname + current.search + current.hash;
    const normalizedVisible = normalized.pathname + normalized.search + normalized.hash;
    if (currentVisible !== normalizedVisible) {
      window.history.replaceState({ pmaCatalogSearch: true }, "", normalizedVisible);
    }
    if (!String(normalized.searchParams.get("q") || "").trim()) return;
    await navigateCanonicalCatalogSearch(normalized, { historyMode: "none", scroll: false });
  }

  window.PMA = window.PMA || {};
  window.PMA.catalogSearch = { navigate: navigateCanonicalCatalogSearch };
  bindCanonicalCatalogSearchInteractions();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initCanonicalCatalogSearch(), { once: true });
  } else {
    initCanonicalCatalogSearch();
  }

  window.addEventListener("popstate", () => {
    if (!isCanonicalCatalogPath()) return;
    const current = normalizeCanonicalCatalogUrl(window.location.href);
    if (String(current.searchParams.get("q") || "").trim()) {
      navigateCanonicalCatalogSearch(current, { historyMode: "none", scroll: false });
      return;
    }
    window.location.reload();
  });
})();
